// core/modals/peoples/SupplierModal.jsx
import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from 'prop-types';
import { Country, State, City } from 'country-state-city';
import { supplierActions } from "../../action/supplierAction";
import { toast } from "react-toastify";

const SupplierModal = ({ mode, supplier, onClose }) => {
  
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    supplierName: "",
    supplierCode: "",
    supplierslug: "",
    email: "",
    phone: "",
    address: "",
    gstNo: "",
    contactPerson: "",
    designation: "",
    website: "",
    notes: "",
    country: null,
    state: null,
    city: null
  });

  const [selectedCountry, setSelectedCountry] = useState({
    label: "India",
    value: "IN"
  });
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load countries on mount
  useEffect(() => {
    setCountries(
      Country.getAllCountries().map(country => ({
        value: country.isoCode,
        label: country.name
      }))
    );
  }, []);

  // Load supplier data if in edit/view mode
  useEffect(() => {
    if (supplier && (mode === 'edit' || mode === 'view')) {
      console.log('Loading supplier data:', supplier); // Debug log
      
      setFormData({
        supplierName: supplier.name || "",
        supplierCode: supplier.code || "",
        supplierslug: supplier.slug || "",
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || "",
        gstNo: supplier.gstNo || "",
        contactPerson: supplier.contact_person || "",
        designation: supplier.designation || "",
        website: supplier.website || "",
        notes: supplier.notes || "",
        country: supplier.country || null,
        state: supplier.state || null,
        city: supplier.city || null
      });

      // Set country
      if (supplier.country) {
        const country = countries.find(c => c.label === supplier.country);
        if (country) {
          setSelectedCountry(country);
        }
      }
    }
  }, [supplier, mode, countries]);

  // Load states when country changes (including initial load from supplier)
  useEffect(() => {
    const loadStatesAndCities = async () => {
      if (selectedCountry) {
        // Load states
        const stateList = State.getStatesOfCountry(selectedCountry.value).map(state => ({
          value: state.isoCode,
          label: state.name
        }));
        setStates(stateList);

        // If we have a supplier with state, select it after states are loaded
        if (supplier && supplier.state && mode !== 'add') {
          const matchingState = stateList.find(s => s.label === supplier.state);
          if (matchingState) {
            setSelectedState(matchingState);
            
            // Load cities for this state
            const cityList = City.getCitiesOfState(selectedCountry.value, matchingState.value).map(city => ({
              value: city.name,
              label: city.name
            }));
            setCities(cityList);

            // If we have a supplier with city, select it after cities are loaded
            if (supplier.city) {
              const matchingCity = cityList.find(c => c.label === supplier.city);
              if (matchingCity) {
                setSelectedCity(matchingCity);
              }
            }
          }
        } else {
          setSelectedState(null);
          setCities([]);
          setSelectedCity(null);
        }
      }
    };

    loadStatesAndCities();
  }, [selectedCountry, supplier, mode]);

  // Load cities when state changes
  useEffect(() => {
    if (selectedState && selectedCountry) {
      const cityList = City.getCitiesOfState(selectedCountry.value, selectedState.value).map(city => ({
        value: city.name,
        label: city.name
      }));
      setCities(cityList);

      // If we have a supplier with city, select it
      if (supplier && supplier.city && mode !== 'add') {
        const matchingCity = cityList.find(c => c.label === supplier.city);
        if (matchingCity) {
          setSelectedCity(matchingCity);
        }
      } else {
        setSelectedCity(null);
      }
    } else {
      setCities([]);
      setSelectedCity(null);
    }
  }, [selectedState, selectedCountry, supplier, mode]);

  // Generate unique supplier code for add mode
  const generateUniqueSupplierCode = async () => {
    if (mode !== 'add') return;
    
    const year = new Date().getFullYear().toString().slice(-2);
    
    const generateRandomPart = () => {
      const letters = Math.random().toString(36).substring(2, 4).toUpperCase();
      const digits = Math.floor(10 + Math.random() * 90);
      return `${letters}${digits}`;
    };

    let candidate = "";
    let attempts = 0;

    while (attempts < 10) {
      candidate = `SUP${year}-${generateRandomPart()}`;
      const exists = await supplierActions.checkSupplierCode(candidate);
      if (!exists) break;
      attempts++;
    }

    if (attempts === 10) {
      toast.error("Couldn't generate a unique Supplier Code. Try again.");
      return;
    }

    setFormData((prev) => ({ ...prev, supplierCode: candidate }));
  };

  // Generate code on mount for add mode
  useEffect(() => {
    if (mode === 'add') {
      generateUniqueSupplierCode();
    }
  }, [mode]);

  // Handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Generate slug from name
    if (name === 'supplierName') {
      setFormData(prev => ({
        ...prev,
        supplierName: value,
        supplierslug: value.replace(/\s+/g, '').toLowerCase()
      }));
    }
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      country: country?.label || null
    }));
    setSelectedCountry(country);
    // Reset state and city when country changes
    setSelectedState(null);
    setSelectedCity(null);
    setCities([]);
  };

  const handleStateChange = (state) => {
    setFormData(prev => ({
      ...prev,
      state: state?.label || null
    }));
    setSelectedState(state);
    // Reset city when state changes
    setSelectedCity(null);
    setCities([]);
  };

  const handleCityChange = (city) => {
    setFormData(prev => ({
      ...prev,
      city: city?.label || null
    }));
    setSelectedCity(city);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.supplierName || formData.supplierName.trim() === '') {
      toast.error('Supplier name is required');
      return;
    }

    setLoading(true);

    const supplierData = {
      SupplierName: formData.supplierName,
      SupplierCode: formData.supplierCode || '',
      SupplierSlug: formData.supplierslug || formData.supplierName.replace(/\s+/g, '').toLowerCase(),
      Email: formData.email || '',
      Phone: formData.phone || '',
      Address: formData.address || '',
      GSTNo: formData.gstNo || '',
      ContactPerson: formData.contactPerson || '',
      Designation: formData.designation || '',
      Website: formData.website || '',
      Notes: formData.notes || '',
      Country: selectedCountry?.label || '',
      State: selectedState?.label || '',
      City: selectedCity?.label || '',
    };

    try {
      let result;
      
      if (mode === 'add') {
        result = await supplierActions.createSupplier(supplierData);
      } else if (mode === 'edit') {
        result = await supplierActions.updateSupplier(supplier.id, supplierData);
      }

      if (result && result.success) {
        toast.success(result.message || `Supplier ${mode === 'add' ? 'added' : 'updated'} successfully`);
        onClose(true);
      } else {
        toast.error(result?.message || `Failed to ${mode} supplier`);
      }
    } catch (error) {
      console.error(`Error ${mode}ing supplier:`, error);
      toast.error(`Failed to ${mode} supplier`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {mode === 'add' && 'Add Supplier'}
              {mode === 'edit' && 'Edit Supplier'}
              {mode === 'view' && 'View Supplier'}
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
          </div>
          
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Supplier Name */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Supplier Name {mode !== 'view' && <span className="text-danger">*</span>}
                  </label>
                  <input 
                    type="text" 
                    name="supplierName" 
                    className="form-control"
                    value={formData.supplierName}
                    onChange={handleInputChange}
                    placeholder="Enter supplier name"
                    disabled={mode === 'view'}
                    required={mode !== 'view'}
                  />
                </div>

                {/* Supplier Code */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Supplier Code</label>
                  <div className="input-group">
                    <input 
                      type="text" 
                      name="supplierCode" 
                      className="form-control"
                      value={formData.supplierCode}
                      onChange={handleInputChange}
                      placeholder="Auto-generated"
                      disabled={true}
                    />
                    {mode === 'add' && (
                      <button 
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={generateUniqueSupplierCode}
                      >
                        Generate
                      </button>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email</label>
                  <input 
                    type="email" 
                    name="email" 
                    className="form-control"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* Phone */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* Contact Person */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Contact Person</label>
                  <input
                    type="text"
                    name="contactPerson"
                    className="form-control"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    placeholder="Enter contact person"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* Designation */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    className="form-control"
                    value={formData.designation}
                    onChange={handleInputChange}
                    placeholder="Enter designation"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* GST Number */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">GST Number</label>
                  <input
                    type="text"
                    name="gstNo"
                    className="form-control"
                    value={formData.gstNo}
                    onChange={handleInputChange}
                    placeholder="Enter GST number"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* Website */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    name="website"
                    className="form-control"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="Enter website URL"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* Address */}
                <div className="col-12 mb-3">
                  <label className="form-label">Address</label>
                  <textarea 
                    name="address" 
                    className="form-control"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                    rows="2"
                    disabled={mode === 'view'}
                  />
                </div>

                {/* Country */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">Country</label>
                  <Select
                    options={countries}
                    value={selectedCountry}
                    onChange={handleCountryChange}
                    placeholder="Select country"
                    isClearable
                    isDisabled={mode === 'view'}
                  />
                </div>

                {/* State */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">State</label>
                  <Select
                    options={states}
                    value={selectedState}
                    onChange={handleStateChange}
                    placeholder="Select state"
                    isClearable
                    isDisabled={mode === 'view' || !selectedCountry}
                  />
                </div>

                {/* City */}
                <div className="col-md-4 mb-3">
                  <label className="form-label">City</label>
                  <Select
                    options={cities}
                    value={selectedCity}
                    onChange={handleCityChange}
                    placeholder="Select city"
                    isClearable
                    isDisabled={mode === 'view' || !selectedState}
                  />
                </div>

                {/* Notes */}
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Notes</label>
                    <textarea
                      name="notes"
                      className="form-control"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Enter additional notes"
                      rows="3"
                      disabled={mode === 'view'}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            {mode !== 'view' && (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Processing...' : mode === 'add' ? 'Add Supplier' : 'Update Supplier'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

SupplierModal.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
  supplier: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default SupplierModal;